import React, { useCallback, useRef, useState } from 'react';
import { ConversationProvider, useConversation } from '@elevenlabs/react';
import { Mic, Loader2, PhoneOff, Keyboard, AlertCircle, Sparkles } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';

// Option B — the in-browser ElevenLabs Conversational AI onboarding. The agent
// handles natural turn-taking / barge-in / streaming voice; OUR backend still
// supplies the script. Two client tools (run here, with the user's session
// token) call the SAME /onboarding/state + /onboarding/turn the tap-or-speak
// flow uses, so the question flow, routing, scoring, and persistence are reused
// unchanged. On done:true the existing /turn has already scored + saved.

type TMsg = { source: 'user' | 'ai'; message: string };

// Compact the onboarding payload into what the agent's tools need: the line to
// speak, the question id, and the *valid values* (so it answers with a value,
// not a label). Returned to the agent as a JSON string (client-tool result).
function compact(data: any): string {
  if (data?.done) return JSON.stringify({ done: true, say: data.say ?? '', payoff: data.payoff ?? undefined });
  const q = data?.question ?? {};
  return JSON.stringify({
    done: false,
    say: data?.say ?? q.say ?? '',
    question_id: q.id ?? '',
    type: q.type ?? 'open',
    options: (q.options ?? []).map((o: any) => o.value),
    why: q.why ?? undefined,
    // ONB-2: the payoff the answer the agent JUST SUBMITTED unlocked — set by
    // the backend only when that answer was accepted (a re-asked question
    // carries none), so the agent says it in one short line before asking the
    // next question and never speaks stale success over a rejected answer.
    payoff: data?.payoff ?? undefined,
    // Why the last answer was NOT accepted (same question re-asked) — the agent
    // relays this instead of implying the answer went through.
    note: data?.note ?? undefined,
  });
}

type QMeta = { type: string; options: { value: string; label?: string }[] } | null;

// Coerce the agent's spoken answer into the structured value the scorer expects:
// a number for scales, a list (of option values) for multi-select, and the
// canonical option value (matched by value OR label) for single/confirm.
function coerceValue(value: unknown, meta: QMeta): unknown {
  if (!meta) return value;
  const opts = meta.options || [];
  const matchOne = (x: unknown) => {
    const s = String(x).trim().toLowerCase();
    const m = opts.find((o) => String(o.value).toLowerCase() === s || String(o.label ?? '').toLowerCase() === s);
    return m ? m.value : x;
  };
  if (meta.type === 'scale') {
    const n = parseInt(String(value).match(/[1-5]/)?.[0] ?? '', 10);
    return Number.isNaN(n) ? value : n;
  }
  if (meta.type === 'multi') {
    const raw = Array.isArray(value) ? value : String(value).split(/[,;]+/);
    return raw.map(matchOne).filter((v) => v !== '' && v != null);
  }
  if (meta.type === 'single' || meta.type === 'confirm') return matchOne(value);
  return value; // open text
}

type Phase = 'idle' | 'connecting' | 'live' | 'error';

const ConversationInner: React.FC<{ onDone: () => void; onExit: (fellBack: boolean) => void }> = ({ onDone, onExit }) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [err, setErr] = useState<string | null>(null);
  const convIdRef = useRef<string | null>(null);
  const transcriptRef = useRef<TMsg[]>([]);
  const doneRef = useRef(false);
  const lastQRef = useRef<QMeta>(null); // meta of the question currently being answered

  const logSession = useCallback((patch: Record<string, unknown>) => {
    aaiApi.logVoiceSession({ conversation_id: convIdRef.current ?? undefined, ...patch }).catch(() => {});
  }, []);

  // Remember the live question's shape (for coercion), flip done, and return the
  // compacted payload to the agent.
  const applyAndCompact = useCallback((data: any): string => {
    if (data?.done) { doneRef.current = true; lastQRef.current = null; }
    else { const q = data?.question ?? {}; lastQRef.current = { type: q.type ?? 'open', options: q.options ?? [] }; }
    return compact(data);
  }, []);

  const conversation = useConversation({
    clientTools: {
      // Names MUST match the agent's client tools (see create_onboarding_agent.py).
      // Prefixed to avoid colliding with the discovery-call's shared tools.
      // The agent calls this to learn what to ask next.
      onboarding_next_question: async () => {
        const { data } = await aaiApi.getOnboardingState();
        return applyAndCompact(data);
      },
      // The agent calls this with the user's answer (coerced to the structured
      // value the scorer expects); it returns the next question so one tool call
      // advances the conversation.
      onboarding_submit_answer: async ({ question_id, value }: { question_id: string; value: unknown }) => {
        const coerced = coerceValue(value, lastQRef.current);
        const { data } = await aaiApi.submitTurn({ question_id, value: coerced as any });
        return applyAndCompact(data);
      },
    },
    onConnect: ({ conversationId }) => {
      convIdRef.current = conversationId;
      setPhase('live');
      logSession({ status: 'in_progress' });
    },
    onMessage: ({ source, message }) => {
      if (message) transcriptRef.current.push({ source, message });
    },
    onError: (message) => { setErr(message || 'Voice connection error.'); setPhase('error'); },
    onDisconnect: () => {
      logSession({
        status: doneRef.current ? 'completed' : 'failed',
        completed_by_voice: doneRef.current,
        transcript: transcriptRef.current,
      });
      if (doneRef.current) onDone();
      else setPhase((p) => (p === 'error' ? p : 'idle'));
    },
  });

  const start = useCallback(async () => {
    setErr(null);
    setPhase('connecting');
    try {
      const { data } = await aaiApi.getVoiceConnection();
      await navigator.mediaDevices.getUserMedia({ audio: true }); // explicit, so we can catch denial
      // The signed URL points at the shared Orbit agent; dynamicVariables.mode
      // flips it into its onboarding branch, and firstMessage overrides the phone
      // greeting with an in-browser one (the agent permits this override).
      conversation.startSession({
        signedUrl: data.signed_url,
        dynamicVariables: data.dynamic_variables,
        overrides: data.first_message ? { agent: { firstMessage: data.first_message } } : undefined,
      });
    } catch (e: any) {
      if (e?.response?.status === 503) { onExit(true); return; }   // not configured → silent fallback
      if (e?.name === 'NotAllowedError' || e?.name === 'NotFoundError') {
        setErr('Orbit needs microphone access to talk with you.');
      } else {
        setErr('Could not start the voice conversation.');
      }
      setPhase('error');
    }
  }, [conversation, onExit]);

  const end = useCallback(() => conversation.endSession(), [conversation]);

  // ── live ────────────────────────────────────────────────────────────────
  if (phase === 'live') {
    return (
      <div className="bg-black text-white rounded-xl p-7 text-center">
        <span className={`mx-auto mb-4 grid place-items-center h-16 w-16 rounded-full bg-brand-500 ${conversation.isSpeaking ? 'animate-pulse' : ''}`}>
          <Sparkles size={26} />
        </span>
        <p className="text-[15px] font-semibold mb-1">
          {conversation.isSpeaking ? 'Orbit is talking…' : 'Listening — just talk'}
        </p>
        <p className="text-[12.5px] text-white/55 mb-5">
          Answer out loud, in your own words. You can interrupt Orbit any time.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button onClick={end}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/15 px-4 py-2 text-[13px] font-semibold whitespace-nowrap">
            <PhoneOff size={15} /> End conversation
          </button>
          <button onClick={() => { conversation.endSession(); onExit(false); }}
            className="inline-flex items-center gap-1.5 text-white/55 hover:text-white text-[12.5px] px-2">
            <Keyboard size={14} /> Type instead
          </button>
        </div>
      </div>
    );
  }

  // ── connecting ────────────────────────────────────────────────────────────
  if (phase === 'connecting') {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-7 text-center">
        <Loader2 size={26} className="mx-auto mb-3 animate-spin text-brand-500" />
        <p className="text-[14px] font-medium text-gray-700">Connecting you to Orbit…</p>
        <p className="text-[12px] text-gray-400 mt-1">Allow microphone access when asked.</p>
      </div>
    );
  }

  // ── error ────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-7">
        <div className="flex items-start gap-2.5 mb-4">
          <AlertCircle size={18} className="text-amber-500 mt-0.5 shrink-0" />
          <p className="text-[13.5px] text-gray-700">{err || 'Something went wrong with the voice conversation.'}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={start}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold px-4 py-2 whitespace-nowrap">
            <Mic size={15} /> Try again
          </button>
          <button onClick={() => onExit(false)}
            className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-[13px]">
            <Keyboard size={14} /> Type instead
          </button>
        </div>
      </div>
    );
  }

  // ── idle (pre-start) ──────────────────────────────────────────────────────
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-7 text-center">
      <span className="mx-auto mb-4 grid place-items-center h-14 w-14 rounded-full bg-brand-50 text-brand-600">
        <Mic size={24} />
      </span>
      <p className="text-[15px] font-semibold text-gray-900 mb-1">Talk it through with Orbit</p>
      <p className="text-[13px] text-gray-500 mb-5 max-w-sm mx-auto">
        A quick, natural ~5-minute chat — Orbit asks, you answer out loud. No tapping required.
      </p>
      <div className="flex items-center justify-center gap-3">
        <button onClick={start}
          className="inline-flex items-center gap-2 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-[14px] font-semibold px-5 py-2.5 whitespace-nowrap">
          <Mic size={16} /> Start the conversation
        </button>
        <button onClick={() => onExit(false)}
          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-[13px]">
          <Keyboard size={14} /> I'll type instead
        </button>
      </div>
    </div>
  );
};

const OrbitConversation: React.FC<{ onDone: () => void; onExit: (fellBack: boolean) => void }> = (props) => (
  <ConversationProvider>
    <ConversationInner {...props} />
  </ConversationProvider>
);

export default OrbitConversation;
